import { LoadingOutlined } from "@ant-design/icons";
import Image, { StaticImageData } from "next/image";
import cx from "classnames";
import useGetPointsTier from "./useGetPointsTier";
import useGetAvatarImg from "./useGetAvatarImg";
import { iconDiamond } from "@/utils/avatar";
import { Star } from "@/assets/icons";

interface AvatarV2Props {
  id: string; // used to pick placeholder
  src: string | StaticImageData;
  alt?: string;
  onMeasured?: () => void; // callback from row to trigger measure
  points?: number;
  square?: boolean; // if true, use square avatar (for pages, circles, etc)
  CGP?: number; // CGP percentage (0-100)
}

const imageCache = new Map<string, boolean>();

const AvatarV2 = ({
  src,
  id,
  alt,
  onMeasured,
  points,
  square,
  CGP,
}: AvatarV2Props) => {
  const { imgUrl, isLoaded, setIsError, setIsLoaded, isLoadingFetch } =
    useGetAvatarImg(src, id, imageCache);
  const { borderStyle, tier } = useGetPointsTier(points || null);

  return (
    <div className="relative">
      <div
        style={{
          ...(!square ? borderStyle : {}),
        }}
        className={`w-14 h-14 overflow-hidden relative ${!square ? "rounded-full" : "rounded-md"}`}
      >
        {!isLoadingFetch && (
          <Image
            src={imgUrl || ""}
            alt={alt || ""}
            fill
            className={cx(
              `object-cover image-avatar ${isLoaded ? "loaded" : ""}`
            )}
            onLoadStart={() => {
              setIsError(false);
            }}
            onLoad={() => {
              // detect if it was not previously cached -> first-time load
              const urlStr = typeof imgUrl === "string" ? imgUrl : "";
              const wasCached = urlStr ? imageCache.get(urlStr) : true;
              imageCache.set(urlStr, true);
              setIsLoaded(true);

              // call onMeasured only if this is the first time we loaded the image
              if (!wasCached && typeof onMeasured === "function") {
                requestAnimationFrame(() => onMeasured());
              }
            }}
            onError={() => {
              setIsError(true);
              // still notify row — its size might change if placeholder differs
              if (typeof onMeasured === "function") {
                requestAnimationFrame(() => onMeasured());
              }
            }}
          />
        )}
        {!isLoaded && (
          <div className="absolute inset-0 flex justify-center items-center bg-transparent bg-slate-700">
            <div className="animate-spin">
              <LoadingOutlined style={{ color: "#158284" }} />
            </div>
          </div>
        )}
        <style jsx>{`
          .image-avatar {
            opacity: 0;
            transition: opacity 0.25s ease;
          }
          .image-avatar.loaded {
            opacity: 1;
          }
        `}</style>
      </div>
      {tier && (
        <span className={`absolute -right-2 bottom-4`}>
          {iconDiamond(tier, "sm")}
        </span>
      )}
      {CGP && CGP !== 0 ? (
        <div
          className={cx(
            "flex justify-center items-center absolute font-bold -bottom-2 -right-2"
          )}
        >
          <Star
            size={32}
            color={
              Number(CGP) >= 80
                ? "green"
                : Number(CGP) >= 51
                  ? "yellow"
                  : "salmon"
            }
          />
          <p
            className={`absolute text-black translate-y-[2px] text-[6px] ${
              Number(CGP) < 75 && "opacity-0"
            }`}
          >{`${Number(CGP).toFixed()}%`}</p>
        </div>
      ): null}
    </div>
  );
};

export default AvatarV2;
